import { Component } from "@angular/core";
import {
  AgentTask,
  AgentTaskSearchParams,
  TaskStatusEnum,
} from "../shared/models/agent-tasks.model";
import { ServiceMetaData } from "../shared/models/service-meta.model";
import { AuthService } from "../shared/services/auth.service";
import { BroshService } from "../shared/services/brosh.service";
import { DataElement, PaginationParams } from "../shared/models/common";
import { Clinic } from "../shared/models/clinic.model";
import { Agent } from "http";
import { DataService } from "../shared/services/data.service";
import { UiUser, UsersSearchParams } from "../shared/models/ui-user.model";
import { UsersService } from "../shared/services/users.service";
import { Store } from "@ngrx/store";
import { GET_GENERAL_TASKS_USERS } from "../shared/ngrx/ngrx-actions";
import {
  agentTasks,
  agentTasksSearchMetadata,
  chatTaskStatusList,
  clinicList,
  generalTaskUsersList,
  taskUsersList,
} from "../shared/ngrx/ngrx-selectors";
import { take, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { SelectModule } from "primeng/select";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { InputWrapComponent } from "../shared/components/ui/input-wrap.component";
import { ModalPopupComponent } from "../shared/components/ui/modal-popup.component";
import { EditAgentTaskComponent } from "./edit-agent-task/edit-agent-task.component";
import { TableComponent } from "../shared/components/ui/table/table.component";
import { LoaderComponent } from "../shared/components/ui/loader/loader.component";
import { ClearMultiComponent } from "../shared/components/ui/clear-multi.component";

@Component({
  selector: "app-agent-tasks",
  templateUrl: "./agent-tasks.component.html",
  styleUrls: ["./agent-tasks.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputWrapComponent,
    TranslateModule,
    SelectModule,
    ModalPopupComponent,
    EditAgentTaskComponent,
    TableComponent,
    LoaderComponent,
    ClearMultiComponent,
  ],
})
export class AgentTasksComponent {
  loading: boolean;
  numberOfRows: number = 15;
  rangeFrom: number = 1;
  rangeTo: number = this.numberOfRows;
  searchParams: AgentTaskSearchParams = new AgentTaskSearchParams();
  displayColumnsList: string[];
  allowedCreateTask: boolean;
  selectedClinic: Clinic;
  selectedUser: UiUser;
  curTask: AgentTask;
  displayEditTask: boolean;
  destroy$ = new Subject();
  clinicList = this.store.selectSignal(clinicList);
  taskUsersList = this.store.selectSignal(generalTaskUsersList);
  statusList = this.store.selectSignal(chatTaskStatusList);

  get data(): AgentTask[] {
    return this.store.selectSignal(agentTasks)();
  }

  get searchMetaData(): ServiceMetaData {
    return this.store.selectSignal(agentTasksSearchMetadata)() || {};
  }

  constructor(
    private readonly store: Store,
    private broshService: BroshService,
    private authService: AuthService,
    private dataService: DataService,
    private usersService: UsersService
  ) { }

  ngOnInit(): void {
    this.allowedCreateTask =
      this.authService.currentUser.authDetails.getBooleanValue(
        "GENERAL_TASKS.CREATE_TASK_SW"
      );

    this.displayColumnsList = [
      "title",
      "clinic",
      "taskUser",
      "crDate",
      "dueDate",
      "createdBy",
      "status",
      "completeDate",
    ];

    this.getData(true);

    this.getTaskUsersList();
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  getTaskUsersList() {
    let userParams = new UsersSearchParams();
    userParams.rangeFrom = 0;
    userParams.rangeTo = 100;
    userParams.generalTasksSw = true;

    this.usersService
      .getUsers(userParams, false, true, false)
      .subscribe((data) => {
        this.store.dispatch(
          GET_GENERAL_TASKS_USERS({
            data: data,
          })
        );
      });
  }

  resetSearchParams() {
    this.searchParams = new AgentTaskSearchParams();
  }

  clearSearch() {
    this.selectedUser = null;
    this.selectedClinic = null;
    this.search();
  }

  search() {
    this.searchParams.taskUserId = Number.parseInt(
      this.selectedUser?.userId.toString()
    );
    this.searchParams.clinicId = Number.parseInt(
      this.selectedClinic?.id.toString()
    );
    this.getData(true);
  }

  getData(newSearch: boolean) {
    if (newSearch) {
      this.searchParams.searchId = null;
      this.searchParams.rangeFrom = 1;
      this.searchParams.rangeTo = this.numberOfRows;
    }

    this.loading = true;
    this.broshService.getAgentTasks(this.searchParams).subscribe(
      () => {
        this.loading = false;
      },
      () => (this.loading = false)
    );
  }

  loadDataOnPagination(event: PaginationParams) {
    if (
      !this.data ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId = this.searchMetaData.searchId;

    this.getData(false);
  }

  createTask() {
    this.curTask = new AgentTask();

    this.curTask.status = this.statusList().find(
      (s) => s.id == TaskStatusEnum.OPEN
    );
    this.displayEditTask = true;
  }

  onRowSelect(row: AgentTask) {
    this.curTask = row;
    this.displayEditTask = true;
  }

  onSave() {
    this.displayEditTask = false;
    this.getData(true);
  }
}
